import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  Stack,
  Avatar,
  CircularProgress,
  useTheme,
} from "@mui/material";
import {
  Send,
  SmartToy,
  Person,
  ContentCopy,
  DeleteOutline,
  AutoAwesome,
} from "@mui/icons-material";
import axios from "axios";
import API_BASE_URL from "../config";

interface Message {
  id: string;
  text: string;
  sender: "user" | "ai";
  timestamp: Date;
}

export default function AIChat(): JSX.Element {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "1",
      text: "Hello! I'm your CoWrite AI Assistant. How can I help you with your academic writing today?",
      sender: "ai",
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: input,
      sender: "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/gemini-generate`, {
        prompt: input,
        model: "gemini-flash-latest",
      }, { timeout: 90000 });

      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        text:
          response.data.result || "I'm sorry, I couldn't process that request.",
        sender: "ai",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      console.error("Chat failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([
      {
        id: Date.now().toString(),
        text: "Chat cleared. How else can I assist you?",
        sender: "ai",
        timestamp: new Date(),
      },
    ]);
  };

  return (
    <Box
      sx={{
        maxWidth: 1000,
        mx: "auto",
        pt: 4,
        height: "calc(100vh - 100px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 600,
              mb: 1,
              trackingTight: "-0.02em",
              color: isDarkMode ? "white" : "text.primary",
            }}
          >
            AI{" "}
            <span style={{ color: isDarkMode ? "#4b5563" : "#3b82f6" }}>
              Assistant
            </span>
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: isDarkMode ? "#9ca3af" : "text.secondary",
              fontWeight: 400,
            }}
          >
            Chat with CoWrite AI to brainstorm, draft, or refine your research
            papers.
          </Typography>
        </Box>
        <IconButton
          onClick={clearChat}
          sx={{
            color: isDarkMode ? "#4b5563" : "text.secondary",
            "&:hover": { color: "#ef4444" },
          }}
        >
          <DeleteOutline />
        </IconButton>
      </Box>

      <Paper
        elevation={0}
        sx={{
          flexGrow: 1,
          bgcolor: isDarkMode ? "#0a0a0a" : "background.paper",
          border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
          borderRadius: "24px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          mb: 3,
        }}
      >
        {/* Messages Area */}
        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          {messages.map((msg) => (
            <Box
              key={msg.id}
              sx={{
                display: "flex",
                justifyContent:
                  msg.sender === "user" ? "flex-end" : "flex-start",
                gap: 2,
              }}
            >
              {msg.sender === "ai" && (
                <Avatar sx={{ bgcolor: "#3b82f6", width: 32, height: 32 }}>
                  <SmartToy fontSize="small" />
                </Avatar>
              )}
              <Box
                sx={{
                  maxWidth: "70%",
                  p: 2,
                  borderRadius:
                    msg.sender === "user"
                      ? "20px 20px 4px 20px"
                      : "4px 20px 20px 20px",
                  bgcolor:
                    msg.sender === "user"
                      ? isDarkMode
                        ? "white"
                        : "primary.main"
                      : isDarkMode
                        ? "rgba(255,255,255,0.03)"
                        : "rgba(0,0,0,0.02)",
                  border:
                    msg.sender === "user"
                      ? "none"
                      : `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color:
                      msg.sender === "user"
                        ? isDarkMode
                          ? "black"
                          : "white"
                        : isDarkMode
                          ? "white"
                          : "text.primary",
                    lineHeight: 1.6,
                  }}
                >
                  {msg.text}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color:
                      msg.sender === "user"
                        ? isDarkMode
                          ? "rgba(0,0,0,0.5)"
                          : "rgba(255,255,255,0.7)"
                        : isDarkMode
                          ? "#4b5563"
                          : "text.secondary",
                    display: "block",
                    mt: 1,
                    fontSize: "10px",
                  }}
                >
                  {msg.timestamp.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </Typography>
              </Box>
              {msg.sender === "user" && (
                <Avatar
                  sx={{
                    bgcolor: isDarkMode
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.05)",
                    width: 32,
                    height: 32,
                    color: isDarkMode ? "white" : "text.secondary",
                  }}
                >
                  <Person fontSize="small" />
                </Avatar>
              )}
            </Box>
          ))}
          {loading && (
            <Box sx={{ display: "flex", gap: 2 }}>
              <Avatar sx={{ bgcolor: "#3b82f6", width: 32, height: 32 }}>
                <SmartToy fontSize="small" />
              </Avatar>
              <Box
                sx={{
                  p: 2,
                  borderRadius: "4px 20px 20px 20px",
                  bgcolor: isDarkMode
                    ? "rgba(255,255,255,0.03)"
                    : "rgba(0,0,0,0.02)",
                  border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
                }}
              >
                <CircularProgress size={16} sx={{ color: "#3b82f6" }} />
              </Box>
            </Box>
          )}
          <div ref={messagesEndRef} />
        </Box>

        {/* Input Area */}
        <Box
          sx={{
            p: 2,
            bgcolor: isDarkMode ? "#030303" : "background.paper",
            borderTop: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              fullWidth
              placeholder="Ask anything..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSend()}
              variant="standard"
              InputProps={{
                disableUnderline: true,
                sx: {
                  bgcolor: isDarkMode
                    ? "rgba(255,255,255,0.03)"
                    : "rgba(0,0,0,0.03)",
                  borderRadius: "12px",
                  px: 2,
                  py: 1,
                  color: isDarkMode ? "white" : "text.primary",
                  fontSize: "14px",
                },
              }}
            />
            <IconButton
              onClick={handleSend}
              disabled={!input.trim() || loading}
              sx={{
                bgcolor: isDarkMode ? "white" : "primary.main",
                color: isDarkMode ? "black" : "white",
                "&:hover": { bgcolor: isDarkMode ? "#e5e7eb" : "primary.dark" },
                "&.Mui-disabled": {
                  bgcolor: isDarkMode
                    ? "rgba(255,255,255,0.1)"
                    : "rgba(0,0,0,0.05)",
                  color: isDarkMode
                    ? "rgba(255,255,255,0.3)"
                    : "rgba(0,0,0,0.25)",
                },
              }}
            >
              <Send fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
      </Paper>

      {/* Suggestion Chips */}
      <Stack
        direction="row"
        spacing={1}
        sx={{
          mb: 2,
          overflowX: "auto",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {[
          "Brainstorm topics",
          "Summarize notes",
          "Fix my abstract",
          "Draft an email",
        ].map((hint) => (
          <Box
            key={hint}
            onClick={() => setInput(hint)}
            sx={{
              px: 2,
              py: 0.75,
              borderRadius: "10px",
              bgcolor: isDarkMode
                ? "rgba(255,255,255,0.03)"
                : "rgba(0,0,0,0.03)",
              border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
              color: isDarkMode ? "#9ca3af" : "text.secondary",
              fontSize: "12px",
              cursor: "pointer",
              whiteSpace: "nowrap",
              transition: "all 0.2s",
              "&:hover": {
                bgcolor: isDarkMode
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.06)",
                color: isDarkMode ? "white" : "primary.main",
              },
            }}
          >
            {hint}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
